import type { ContextSchema } from '@/vantage/types/Context.types';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import _ from 'lodash';
import type { ColumnDefinitionOutputV1 } from '@/sdk/model/ColumnDefinitionOutputV1';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import { VantageNames } from '@/vantage/constants';
import type { ColumnRuleInputWithUIMetadata } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import { getColumnHeaderName } from '@/vantage/components/evidence_table/helpers/column.utilities';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import {
  vantageColumnConfigTabId,
  vantageColumnConfigTabJson,
  vantageConditionMonitorTabId,
  vantageConditionMonitorTabJson,
} from '@/main/layoutDefinitions';
import type { Model } from 'flexlayout-react';
import { Actions, DockLocation } from 'flexlayout-react';
import type { AnyProperty } from '@/utilities.types';

export const getInitials = (fullName: string) => {
  const parts = fullName.split(' ');
  const first = parts[0].charAt(0);
  const last = parts[parts.length - 1].charAt(0);
  return (first + last).toUpperCase();
};

export function generateIncrementingStrings(prefix: string, suffix: string, start: number, count: number): string[] {
  const result: string[] = [];

  for (let i = 0; i < count; i++) {
    const currentNumber: number = start + i;
    const currentString = `${prefix}${currentNumber}${suffix}`;
    result.push(currentString);
  }

  return result;
}

export function createObjectFromLists(listA: string[], listB: string[]): { [key: string]: string } {
  const result: { [key: string]: string } = {};

  for (let i = 0; i < listA.length; i++) {
    const key: string = listA[i];
    const value: string = listB[i % listB.length];
    result[key] = value;
  }

  return result;
}

export function findIntersection(allArrays: ContextSchema[][], field: keyof ContextSchema) {
  const allMaps = allArrays.map((arr) => arr.reduce((map, obj) => map.set(obj[field], obj), new Map()));
  return allArrays[0].filter((obj) => allMaps.every((map) => map.has(obj[field])));
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function kindRepresentation(kind: string) {
  return capitalizeFirstLetter(kind);
}

export function hasKey(obj: object, key: string) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function sortExtensions(arr: ExtensionSchema[]) {
  arr.sort((a, b) => {
    const aSortOrder =
      a.uiConfig?.consoleToolbarConfiguration?.sortOrder !== undefined
        ? a.uiConfig?.consoleToolbarConfiguration?.sortOrder
        : Infinity;
    const bSortOrder =
      b.uiConfig?.consoleToolbarConfiguration?.sortOrder !== undefined
        ? b.uiConfig?.consoleToolbarConfiguration?.sortOrder
        : Infinity;
    return aSortOrder - bSortOrder;
  });

  return arr;
}

export const interpolateExtensionText = (template: string, payload: AnyProperty, value: any) => {
  return template
    .replace(/\$\{value\}|\{\{value\}\}/g, value ?? '')
    .replace(/\$\{payload\.([\w.]+)\}/g, (_match, propertyPath) => (payload && _.get(payload, propertyPath)) ?? '')
    .replace(/\{\{payload\.([\w.]+)\}\}/g, (_match, propertyPath) => (payload && _.get(payload, propertyPath)) ?? '')
    .replace(/\{\{payload(\w+)\}\}/g, (_match, propertyNameCapitalized) => {
      const propertyName = propertyNameCapitalized.charAt(0) + propertyNameCapitalized.slice(1);
      return (payload && Object.hasOwnProperty.call(payload, propertyName) && payload[propertyName]) ?? '';
    });
};

// https://stackoverflow.com/a/38340730
export const removeEmpty = (obj: AnyProperty): AnyProperty => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, v]) => v != null)
      .map(([k, v]) => [k, v === Object(v) ? removeEmpty(v) : v]),
  );
};

export const dedupeCapsuleRanges = (notes: ContextSchema[]): ContextSchema[] => {
  // Group the notes with 'kind' field of 'Capsule'
  const capsuleGrouped = _.groupBy(
    notes.filter((note) => note.kind === VantageNames.Kinds.Capsule),
    (item) => `${item.content.value}_${item.seeqUser}_${item.timeCreated}`,
  );

  // Dedupe the 'Capsule' notes and adjust eventStart and eventEnd
  const deduplicatedCapsules = _.map(capsuleGrouped, (items) => {
    const firstItem = items[0];
    const earliestEventStart = _.minBy(items, (item) => new Date(item.eventStart!))?.eventStart;
    const latestEventEnd = _.maxBy(items, (item) => new Date(item.eventEnd!))?.eventEnd;
    return {
      ...firstItem,
      eventStart: earliestEventStart,
      eventEnd: latestEventEnd,
      duplicates: items.length - 1,
    };
  }) as ContextSchema[];

  const otherNotes = notes
    .filter((note) => note.kind !== VantageNames.Kinds.Capsule)
    .map((note) => ({
      ...note,
      duplicates: 0,
    })) as ContextSchema[];

  return _.sortBy([...deduplicatedCapsules, ...otherNotes], (item) => new Date(item.timeCreated));
};

export const conditionScopes = async (capsules: any[]): Promise<any[]> => {
  const leafPairs = _.uniqWith(
    capsules.flatMap((capsule) =>
      Object.entries(capsule)
        .filter(([key]) => key.match(/scoped ?to/i))
        .map(([, scopedTo]) => ({ scopedTo, conditionId: capsule[VantageNames.Columns.ConditionID] })),
    ),
    _.isEqual,
  );
  if (leafPairs.length > 0) return leafPairs;

  const conditions = await Promise.all(
    _.uniq(capsules.map((capsule) => capsule[VantageNames.Columns.ConditionID])).map((id) =>
      sqItemsApi.getItemAndAllProperties({ id }).then(({ data }) => data),
    ),
  );
  const conditionIdToScopeId = Object.fromEntries(conditions.map(({ id, scopedTo }) => [id, scopedTo]));
  const conditionPairs = _.uniqWith(
    capsules.flatMap((capsule) => {
      const conditionId = capsule[VantageNames.Columns.ConditionID];
      const scopedTo = conditionIdToScopeId[conditionId];
      return conditionId && scopedTo ? [{ conditionId, scopedTo }] : [];
    }),
    _.isEqual,
  );
  if (conditionPairs.length > 0) return conditionPairs;

  return [];
};

export const transformColumns = (columns: ColumnDefinitionOutputV1[]): ColumnDefinition[] =>
  columns.map((column) => ({
    ...column,
    displayName: getColumnHeaderName(column.columnName),
  }));

export const getDefaultColumnRules = (name: string): ColumnRuleInputWithUIMetadata[] => {
  return [
    {
      eventProperty: {
        propertyName: name,
      },
    },
    {
      getItemProperty: {
        columnIndex: 1,
        propertyName: name,
      },
    },
  ];
};

export const getValueFromKeyMapping = (key: string, obj: AnyProperty<any>, uniquenessMapping: Record<string, string>) =>
  hasKey(uniquenessMapping, key) ? obj[uniquenessMapping[key]] : obj[key];

/**
 * Adds a tab to the layout model if it doesn't exist, or selects it if it does.
 * @param layoutModel The layout model to add the tab to.
 * @param tabId The ID of the tab to add or select.
 * @param tabJson The JSON representation of the tab to add.
 * @param parentId The ID of the parent node to add the tab to.
 * @param location The location of the tab within the parent node.
 */
export const addOrSelectVantageTab = (
  layoutModel: Model,
  tabId: string,
  tabJson: object,
  parentId = 'mainTab',
  location: DockLocation = DockLocation.CENTER,
): void => {
  if (layoutModel.getNodeById(tabId)) {
    layoutModel.doAction(Actions.selectTab(tabId));
  } else {
    layoutModel.doAction(Actions.addNode(tabJson, parentId, location, -1));
  }
};

/**
 * Convenience function to select the Vantage conditions tab
 * @param layoutModel The layout model to select the tab from.
 */
export const selectVantageConditionsTab = (layoutModel: Model): void =>
  addOrSelectVantageTab(layoutModel, vantageConditionMonitorTabId, vantageConditionMonitorTabJson);

/**
 * Convenience function to select the Vantage columns tab
 * @param layoutModel The layout model to select the tab from.
 */
export const selectVantageColumnsTab = (layoutModel: Model): void =>
  addOrSelectVantageTab(layoutModel, vantageColumnConfigTabId, vantageColumnConfigTabJson);

/**
 * Formats parts into a single element ID string by joining with hyphens.
 * @param parts - parts of the element ID
 * @returns The formatted element ID string
 */
export const formatElementId = (...parts: Array<string | number | undefined | null>): string =>
  parts.filter((part) => part !== undefined && part !== null && part !== '').join('-');

/**
 * Finds the full user-defined context column name/ID given a short column name/ID.
 *
 * @param columnName - The short column name/ID to find.
 * @param userDefinedContextColumnNames - The list of full user-defined context column names/IDs.
 *
 * @returns The full column name if found, otherwise undefined.
 */
export const getUserDefinedContextColumnFullName = (
  columnName: string,
  userDefinedContextColumnNames: string[],
): string | undefined =>
  userDefinedContextColumnNames.find((columnFullName) =>
    columnFullName.endsWith(`.${columnName.replace(new RegExp(` \\(.*\\)$`), '')}`),
  );
